import { ErrorFeedback } from './components/ErrorFeedback.tsx';
import { runUiAction } from '../state/feedback.ts';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../persistence/db.ts';
import { t } from '../i18n/he.ts';
import { formatBuildTime } from '../lib/format.ts';
import { useSyncStore } from '../state/sync.ts';
import { CONTENT_WIDTH } from './layout.ts';
import * as repo from '../persistence/repo.ts';
import { SyncBadge } from './components/SyncBadge.tsx';
import { ThemeToggle } from './components/ThemeToggle.tsx';

const ImportView = lazy(() => import('./views/ImportView.tsx').then((module) => ({ default: module.ImportView })));
const TransactionsView = lazy(() => import('./views/TransactionsView.tsx').then((module) => ({ default: module.TransactionsView })));
const ReviewView = lazy(() => import('./views/ReviewView.tsx').then((module) => ({ default: module.ReviewView })));
const SettingsView = lazy(() => import('./views/SettingsView.tsx').then((module) => ({ default: module.SettingsView })));
const ChartsView = lazy(() => import('./views/ChartsView.tsx').then((module) => ({ default: module.ChartsView })));

type Tab = 'charts' | 'transactions' | 'review' | 'import' | 'settings';

const TAB_LABELS: Record<Tab, string> = {
  charts: t.charts.tabDashboard,
  transactions: t.app.tabTransactions,
  review: t.app.tabReview,
  import: t.app.tabImport,
  settings: t.app.tabSettings,
};

const TABS = Object.keys(TAB_LABELS) as Tab[];

const TAB_KEY = 'expenses.ui.tab';

function initialTab(): Tab {
  const stored = sessionStorage.getItem(TAB_KEY);
  return TABS.includes(stored as Tab) ? (stored as Tab) : 'transactions';
}

export function App() {
  const [tab, setTab] = useState<Tab>(initialTab);
  const initSync = useSyncStore((state) => state.init);

  useEffect(() => {
    sessionStorage.setItem(TAB_KEY, tab);
  }, [tab]);

  useEffect(() => {
    void runUiAction(initSync);
  }, [initSync]);

  useEffect(() => {
    void runUiAction(repo.seedDefaultsIfEmpty);
  }, []);

  /*
   * Only the uncategorized count now — the row count, statement count and
   * running total used to live up here too, but the transactions toolbar
   * already states all three for the current filter, and a second copy that
   * ignores the filter invites the question of which one is wrong.
   *
   * A count query rather than `toArray()`: the old version pulled every
   * transaction into memory on each change just to reduce it to four numbers.
   */
  const uncategorized = useLiveQuery(
    () => db.transactions.filter((tx) => tx.categoryId === null).count(),
    [],
  );

  return (
    <div className="flex h-full flex-col bg-slate-100/50 dark:bg-slate-950">
      {/*
        * Wrapping, not one fixed row. Brand + five tabs + the stat cluster is
        * about 510px of content, and the document is `overflow: hidden`, so on
        * a phone the overflow was clipped off the start edge rather than
        * scrolled to. The nav takes a full-width row of its own below the
        * brand until sm, where everything fits inline again.
        */}
      <header className="shrink-0 border-b border-slate-800 bg-slate-900 px-3 py-2 text-white shadow-sm sm:px-5 sm:py-2.5 dark:border-slate-800/80 dark:bg-slate-950">
        {/*
          * The bar itself is full-bleed — it is chrome, and a dark band that
          * stops short of the window edge looks like a mistake — but its
          * contents sit in the same column as the view below. Without the cap
          * `ms-auto` threw the account controls to the far edge, leaving a void
          * between them and the tabs that grew with the screen: 409px on a
          * 1213px window, over a thousand at 1920, above a centred card.
          */}
        <div className={`${CONTENT_WIDTH} flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-4`}>
        <div className="order-1 flex shrink-0 items-center gap-2.5">
          {/*
            * The favicon itself, so the tab and the header carry one mark
            * instead of two unrelated ones. Served from `public/` — same
            * origin, which is all the CSP's `img-src 'self' data:` allows —
            * and already fetched as the tab icon, so it costs no extra
            * request. No gradient tile behind it: the file draws its own
            * rounded background, and wrapping it produced a badge inside a
            * badge. Decorative, so `alt=""` — the <h1> beside it already
            * names the app, and a screen reader announcing it twice is noise.
            *
            * No `rounded-*` or `shadow-*` either: the mark is a circle on a
            * transparent square, so a corner radius clips nothing and a box
            * shadow would trace the square the artwork does not fill.
            */}
          <img
            src="/favicon.png"
            alt=""
            width={32}
            height={32}
            className="h-8 w-8 shrink-0"
          />
          <h1 className="text-sm font-bold tracking-tight text-white sm:text-base">{t.app.title}</h1>
        </div>

        {/*
          * The nav sits against the brand, not centred. `sm:mx-auto` used to
          * split the slack either side of it, which on a 1440px window meant
          * two 256px voids and a bar that read as three islands. Worse, it was
          * never actually centred: the account cluster is ~355px against the
          * brand's ~89px, so equal margins put the tabs 133px right of the
          * column's centre — close enough to look like a mistake rather than a
          * choice. Anchored here, the brand and tabs line up with the start of
          * the view below and the account controls with its end, so the header
          * and the toolbar under it share one set of edges.
          */}
        <nav className="order-3 flex w-full gap-1 overflow-x-auto rounded-xl border border-slate-700/60 bg-slate-800/80 p-1 shadow-inner sm:order-2 sm:w-auto dark:border-slate-800 dark:bg-slate-900/80">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`shrink-0 whitespace-nowrap rounded-lg px-2.5 py-1 text-xs font-medium transition-all sm:px-3.5 ${
                tab === value
                  ? 'bg-indigo-600 text-white shadow-xs shadow-indigo-900/50 ring-1 ring-indigo-400/30'
                  : 'text-slate-300 hover:bg-slate-700/70 hover:text-white'
              }`}
            >
              {TAB_LABELS[value]}
            </button>
          ))}
        </nav>

        {/*
          * The theme toggle and sync badge are chrome, not statistics, so they
          * are not gated on a Dexie query — appearing only once the first read
          * resolves makes them look like they loaded late.
          */}
        {/* `ms-auto` at every width: below sm the nav is on its own row and
            there is nothing else to push this group to the end, and from sm it
            is what collects the bar's slack into one gap before the account
            controls rather than two around the tabs. */}
        <div className="order-2 ms-auto flex shrink-0 items-center gap-2 text-xs sm:order-3 sm:gap-3">
          <ThemeToggle />
          <SyncBadge />

          {/*
            * The one number that stays: it is a call to action rather than a
            * statistic, it is absent whenever the queue is empty, and nothing
            * else in the app surfaces it.
            */}
          {uncategorized !== undefined && uncategorized > 0 && (
            <button
              type="button"
              onClick={() => setTab('review')}
              title={t.app.statUncategorized}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1 font-medium text-amber-300 transition-all hover:bg-amber-500/20 sm:px-2.5"
            >
              <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="numeric">{uncategorized}</span>
              <span className="hidden sm:inline">{t.app.statUncategorized}</span>
            </button>
          )}
        </div>
        </div>
      </header>

      <ErrorFeedback />
      <main className="min-h-0 flex-1 overflow-hidden">
        <Suspense fallback={<div role="status" className="p-6 text-sm text-slate-500">טוען…</div>}>
          {tab === 'charts' && <ChartsView />}
          {tab === 'transactions' && <TransactionsView />}
          {tab === 'review' && <ReviewView />}
          {tab === 'import' && <ImportView />}
          {tab === 'settings' && <SettingsView />}
        </Suspense>
      </main>

      {/*
        * Which build is running.
        *
        * `shrink-0` so it keeps its height against the flex-1 main above it,
        * and outside <main> so it does not scroll away with a view's content —
        * the reason to look at it is usually "did my deploy actually land",
        * which is a question you ask from wherever you happen to be.
        *
        * `.numeric` on the stamp: it is digit groups, and under the RTL
        * document they would otherwise be reordered.
        */}
      <footer
        className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-1.5 text-[11px] text-slate-400 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-500"
        title={t.app.buildTitle(__BUILD_TIME__)}
      >
        <div className={`${CONTENT_WIDTH} flex justify-end gap-1.5`}>
          <span>{t.app.buildLabel}</span>
          <span className="numeric">{formatBuildTime(__BUILD_TIME__)}</span>
        </div>
      </footer>
    </div>
  );
}
